// Local library
import {
  CardDatePipe,
  CardNamePipe,
  CardNumEndPipe,
  IsEmptyPipe,
  UnsubscribeObservableService,
  TranslatePipe
} from '@amwager/util';

// Angular
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnInit
} from '@angular/core';
import {
  FormGroup,
  ReactiveFormsModule
} from '@angular/forms';
import { CommonModule } from '@angular/common';

// Material
import { MatIconModule } from '@angular/material/icon';

// Local funding
import { DepositDebitCreditCardService, IDebitCreditCard } from '@amwager/funding/data-access';

// Third party
import { Observable, Subscription, of, takeUntil } from 'rxjs';

// Local file
import { AddAccountCardComponent } from './add-account-card/add-account-card.component';

@Component({
  selector: 'feature-mat-debit-credit-card',
  templateUrl: './debit-credit-card.component.html',
  styleUrls: ['./debit-credit-card.component.scss'],
  imports: [
    CommonModule,
    MatIconModule,
    ReactiveFormsModule,
    AddAccountCardComponent,
    IsEmptyPipe,
    CardNamePipe,
    CardDatePipe,
    CardNumEndPipe,
    TranslatePipe
  ],
  standalone: true,
  providers: [DepositDebitCreditCardService],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DebitCreditCardComponent extends UnsubscribeObservableService implements OnInit {

  showAddCard: boolean = false;

  debitAndCreditCardForm!: FormGroup;

  // convenience getter for easy access to form fields
  get formVal() { return this.debitAndCreditCardForm?.controls }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private eProtectiframeClient: any;

  private subsAddCardResponse!: Subscription;

  private subsDeleteCardResponse!: Subscription;

  orderId?: string;

  cards$!: Observable<IDebitCreditCard[]>;

  private subsGetCardResponse!: Subscription;

  showCardList = false;
  amount!: number;

  awaitFee: boolean = false;
  fee!: number;
  total!: number;

  private tmsId?: string;

  private subsDepositCardResponse!: Subscription;

  isDepositing = false;
  isOnline: boolean = true;

  constructor(
    private readonly elementRef: ElementRef,
    private readonly cdRef: ChangeDetectorRef,
    private readonly debitCreditCardService: DepositDebitCreditCardService,
  ) {
    super();

    // OLD (moved to DepositDebitCreditCardService.isAddCardSuccess$):
    // this.subsAddCardResponse = this.fundingFacade.isAddDebitCreditCardSuccess$()
    this.subsAddCardResponse = this.debitCreditCardService.isAddCardSuccess$()
    .pipe(takeUntil(this.toUnsubscribe$))
    .subscribe(({response, isSuccess}) => {

      if (!isSuccess) return;

      this.showAddCard = false;
      this.cdRef.detectChanges();
    });


    // OLD (moved to DepositDebitCreditCardService.cardFee$):
    // this.fundingFacade.getCardFee$()
    this.debitCreditCardService.cardFee$()
      .pipe(takeUntil(this.toUnsubscribe$))
      .subscribe((cardFee) => {

        this.fee = cardFee?.Fee as number;
        this.total = cardFee?.TotalAmount || this.amount;
        this.awaitFee = false;

        this.cdRef.markForCheck();
      });
  }



  ngOnInit() {
    this.checkConnectivity();
    this.observerTmsIds();

    // OLD (moved to DepositDebitCreditCardService.buildForm):
    // this.initForm();
    this.debitAndCreditCardForm = this.debitCreditCardService.buildForm();

    this.fee = 0;
    this.total = this.amount;

    // OLD (getDebitCreditCard + list stream moved to DepositDebitCreditCardService.loadCards$):
    // this.fundingFacade.getDebitCreditCard();
    // this.subsGetCardResponse = this.fundingFacade.getListCards$()
    this.subsGetCardResponse = this.debitCreditCardService.loadCards$()
    .pipe(takeUntil(this.toUnsubscribe$))
    .subscribe((response) => {
      if (response !== undefined) {
        this.cards$ = of(response);
        this.showCardList = true;
      }
      this.cdRef.markForCheck();
    });
  }

  showAddCardFn(isShow: boolean): void {
    if (!this.isOnline) {
      // OLD (moved to DepositDebitCreditCardService.setOffline):
      // this.commonFacade.setIsConnectedInternet('DebitCreditCardComponent:showAddCardFn', false);
      this.debitCreditCardService.setOffline('DebitCreditCardComponent:showAddCardFn');
    }

    this.showAddCard = isShow;
    this.cdRef.markForCheck();
  }

  private observerTmsIds(): void {
    // OLD (moved to DepositDebitCreditCardService.tmsId$):
    // this.commonFacade?.tmsId$()
    this.debitCreditCardService.tmsId$()
      .pipe(takeUntil(this.toUnsubscribe$))
      .subscribe((tmsId: string | undefined) => {
        this.tmsId = tmsId;
      });
  }

  chooseAmount(amount: number): void {
    this.formVal['amount']?.setValue(amount);
    this.amount = amount;

    this.fee = 0;
    this.total = this.amount;

    // OLD (moved to DepositDebitCreditCardService.setCardFee):
    // if (...) this.fundingFacade.setCardFee(true, this.amount, 'CreditCard');
    if (this.orderId && this.amount > 0 && this.formVal['amount'].valid) this.debitCreditCardService.setCardFee(this.amount);
    else {
      this.fee = 0;
      this.total = this.amount;
    }

    this.awaitFee = true;
  }

  showAlertMessage(header: string, message: string): void {
    // OLD (moved to DepositDebitCreditCardService.showAlert):
    // this.matNotificationService.showAlert(header, message, undefined, undefined, 'Close');
    this.debitCreditCardService.showAlert(header, message);
  }

  confirmRemoveCard(orderId: string): void {
    // OLD (confirmation alert moved to DepositDebitCreditCardService.confirmRemoveCard$):
    // this.matNotificationService.showAlert('Confirmation', '...', undefined, 'Yes', 'Cancel')
    this.debitCreditCardService.confirmRemoveCard$()
    .subscribe((isYes: boolean) => {
      if (isYes) this.removeCard(orderId);
    });
  }

  removeCard(orderId: string): void {
    // OLD (deleteDebitCreditCard + success stream moved to DepositDebitCreditCardService.delete$):
    // this.fundingFacade.deleteDebitCreditCard(orderId);
    // this.subsDeleteCardResponse = this.fundingFacade.isDeleteDebitCreditCardSuccess$()
    this.subsDeleteCardResponse = this.debitCreditCardService.delete$(orderId)
    .pipe(takeUntil(this.toUnsubscribe$))
    .subscribe(({response, isSuccess}) => {
      if (!isSuccess) {
        this.showAlertMessage('Error', response.error.Message);
      }
      this.subsDeleteCardResponse?.unsubscribe();
    });
  }


  addCard(): void {
    // OLD (session id generation moved to DepositDebitCreditCardService.buildRegistrationMessage):
    // const id = this.sessionService.generateSessionId();
    // const orderId = this.sessionService.generateSessionId();
    // const message = { "id": btoa(id).substring(0,24), "orderId": btoa(orderId).substring(0, 24) };
    const message = this.debitCreditCardService.buildRegistrationMessage();

    if (message.id) {
      this.eProtectiframeClient.getPaypageRegistrationId(message);
    }
  }


  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  chooseCard($event: any, method: string, card: string, orderId: string): void {

    const cardRows = this.elementRef.nativeElement.querySelectorAll(
      '.card-per-row'
    );
    cardRows.forEach((cardRow: HTMLElement) => {
      cardRow.classList.remove('active');
    });

    const creditCard = this.elementRef.nativeElement.querySelector(
      '.card-content .card-list-container #'
      + card
      + '.card-per-row'
    );

    this.fee = 0;
    this.total = this.amount;

      if (creditCard.classList.contains('active')) {
        creditCard.classList.remove('active');
        this.orderId = '';

        this.fee = 0;
        this.total = this.amount;
      }
      else {
        creditCard.classList.add('active');
        this.orderId = orderId;

        // OLD (moved to DepositDebitCreditCardService.setCardFee):
        // if (...) this.fundingFacade.setCardFee(true, this.amount, 'CreditCard');
        if (this.orderId && this.amount > 0 && this.formVal['amount'].valid) this.debitCreditCardService.setCardFee(this.amount);
        else {
          this.fee = 0;
          this.total = 0;
        }

      }
  }


  depositAmount(): void {
    this.amount = this.formVal['amount']?.value;

    this.fee = 0;
    this.total = this.amount;

    setTimeout(() => {
      // OLD (moved to DepositDebitCreditCardService.setCardFee):
      // if (...) this.fundingFacade.setCardFee(true, this.amount, 'CreditCard');
      if (this.orderId && this.amount > 0 && this.formVal['amount'].valid) this.debitCreditCardService.setCardFee(this.amount);
      else {
        this.fee = 0;
        this.total = this.amount;
      }
    }, 300);

    this.awaitFee = true;
  }

  deposit(): void {
    if (!this.isOnline) {
      // OLD (moved to DepositDebitCreditCardService.setOffline):
      // this.commonFacade.setIsConnectedInternet('DebitCreditCardComponent:deposit', false);
      this.debitCreditCardService.setOffline('DebitCreditCardComponent:deposit');
      return;
    }

    if (this.debitAndCreditCardForm.valid && !this.awaitFee && this.amount && this.orderId && this.tmsId) {
      this.isDepositing = true;

      // OLD (dispatch + success stream moved to DepositDebitCreditCardService.deposit$):
      // this.fundingFacade.depositDebitCreditCard(this.amount, this.orderId, this.tmsId);
      // this.subsDepositCardResponse = this.fundingFacade.isDepositDebitCreditCardSuccess$()
      this.subsDepositCardResponse = this.debitCreditCardService.deposit$(this.amount, this.orderId, this.tmsId)
      .pipe(takeUntil(this.toUnsubscribe$))
      .subscribe(({response, isSuccess}) => {
        if (!isSuccess && response !== undefined) {
          this.showAlertMessage('Deposit Error', response.error.Message);
          this.isDepositing = false;
          this.cdRef.markForCheck();
        }


        this.subsDepositCardResponse?.unsubscribe();

        if (isSuccess) {
          this.showAlertMessage('', 'Successfully Deposited Amount');
          this.debitAndCreditCardForm.reset();
          this.isDepositing = false;
          this.cdRef.markForCheck();
        }
      });
    }
    else {
      // OLD (moved to DepositDebitCreditCardService.markFormGroupTouched):
      // this.fundingService.markFormGroupTouched(this.debitAndCreditCardForm);
      this.debitCreditCardService.markFormGroupTouched(this.debitAndCreditCardForm);
    }
  }

  checkConnectivity(): void {
    // OLD (moved to DepositDebitCreditCardService.checkConnectivity):
    // this.fundingService.checkConnectivity(
    //   this.environment,
    //   'DebitCreditCardComponent:checkConnectivity',
    //   (online: boolean) => this.isOnline = online
    // );
    this.debitCreditCardService.checkConnectivity(
      'DebitCreditCardComponent:checkConnectivity',
      (online: boolean) => this.isOnline = online
    );
  }

}
